import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Box, Typography, IconButton, Paper,
  CircularProgress, Alert
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { startOfWeek, endOfWeek, addWeeks, addDays, format, isToday } from 'date-fns';
import { getMySchedule } from '../../collections/workSchedules';
import DayScheduleCard from './DayScheduleCard';

const MySchedule = ({ setPageTitle }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);

  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Lịch Của Tôi');
    }
  }, [setPageTitle]);

  const loadMySchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMySchedule(
        format(weekStart, 'yyyy-MM-dd'),
        format(weekEnd, 'yyyy-MM-dd')
      );
      setScheduleData(data.schedules || []);
    } catch (err) {
      setError('Không thể tải lịch làm việc. Vui lòng thử lại.');
      console.error('Error loading schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    loadMySchedule();
  }, [loadMySchedule]);

  const goToPreviousWeek = () => setCurrentDate(addWeeks(currentDate, -1));
  const goToNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const goToThisWeek = () => setCurrentDate(new Date());

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const getScheduleForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return scheduleData.find(s => s.date === dateStr);
  };

  return (
    <Container maxWidth="sm" sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Lịch Làm Việc Của Tôi
        </Typography>
      </Box>

      {/* Week Navigator */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <IconButton onClick={goToPreviousWeek} aria-label="Tuần trước">
          <ChevronLeft />
        </IconButton>

        <Box textAlign="center">
          <Typography variant="body1" fontWeight={500}>
            {format(weekStart, 'dd/MM')} - {format(weekEnd, 'dd/MM/yyyy')}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={goToThisWeek}
          >
            Tuần hiện tại
          </Typography>
        </Box>

        <IconButton onClick={goToNextWeek} aria-label="Tuần sau">
          <ChevronRight />
        </IconButton>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Schedule Cards */}
      {!loading && !error && (
        <Box>
          {weekDays.map(day => (
            <DayScheduleCard
              key={format(day, 'yyyy-MM-dd')}
              date={day}
              schedule={getScheduleForDate(day)}
              isToday={isToday(day)}
            />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default MySchedule;
